import { Injectable, inject, effect, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from './auth.service';

interface ChatBotConfig {
  configUrl: string;
  widgetUrl: string;
}

type ChatWidgetWindow = Window &
  typeof globalThis & {
    CHAT_WIDGET_CONFIG?: unknown;
  };

const CHAT_WIDGET_OWNER = 'rdc-chat-widget';
const CHAT_WIDGET_OWNER_ATTR = 'data-chat-widget-owner';
const CHAT_WIDGET_SEQUENCE_ATTR = 'data-chat-widget-seq';
const CHAT_WIDGET_ARTIFACT_SELECTOR =
  '#cw-toggle, #cw-window, #cw-style, #cw-fonts, #cw-widget-config';
const CHAT_WIDGET_RETRY_DELAY_MS = 1000;
const CHAT_WIDGET_MAX_RETRIES = 1;

const AUTHENTICATED_BOT: ChatBotConfig = {
  configUrl: 'https://stfuncsrcchatbotproxy.z1.web.core.windows.net/chat-widget.config.js',
  widgetUrl: 'https://stfuncsrcchatbotproxy.z1.web.core.windows.net/chat-widget.js',
};

const PUBLIC_BOT: ChatBotConfig = {
  configUrl: 'https://stfuncsrcchatbotanproxy.z1.web.core.windows.net/chat-widget.config.js',
  widgetUrl: 'https://stfuncsrcchatbotanproxy.z1.web.core.windows.net/chat-widget.js',
};

@Injectable({ providedIn: 'root' })
export class ChatWidgetService {
  private readonly authService = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);

  private loadedScripts: HTMLScriptElement[] = [];
  private currentBotKey: string | null = null;
  private loadSequence = 0;
  private retryAttempts = new Map<string, number>();
  private retryTimeoutId: number | null = null;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    effect(() => {
      // Only checks for a valid token — payment status and contract signature are intentionally ignored
      const config = this.authService.isAuthenticated() ? AUTHENTICATED_BOT : PUBLIC_BOT;
      this.loadBot(config);
    });
  }

  private loadBot(config: ChatBotConfig): void {
    const botKey = `${config.configUrl}|${config.widgetUrl}`;
    if (this.currentBotKey === botKey) return;

    if (this.retryTimeoutId !== null) {
      window.clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    const loadSequence = ++this.loadSequence;

    this.removeCurrentScripts();
    this.removeCurrentWidgetArtifacts();
    this.clearWidgetConfig();

    const configScript = this.createScript(config.configUrl);
    configScript.id = 'cw-widget-config';
    configScript.onload = () => {
      if (loadSequence !== this.loadSequence) return;

      const widgetScript = this.createScript(config.widgetUrl);
      widgetScript.onload = () => {
        this.handleWidgetLoaded(botKey, loadSequence);
      };
      widgetScript.onerror = () => {
        this.handleLoadFailure(botKey, loadSequence, config);
      };
      document.head.appendChild(widgetScript);
      this.loadedScripts.push(widgetScript);
    };
    configScript.onerror = () => {
      this.handleLoadFailure(botKey, loadSequence, config);
    };

    document.head.appendChild(configScript);
    this.loadedScripts = [configScript];
  }

  private removeCurrentScripts(): void {
    this.loadedScripts.forEach((script) => {
      script.onload = null;
      script.onerror = null;
      script.remove();
    });
    this.loadedScripts = [];
  }

  private removeCurrentWidgetArtifacts(): void {
    document
      .querySelectorAll(`[${CHAT_WIDGET_OWNER_ATTR}="${CHAT_WIDGET_OWNER}"]`)
      .forEach((element) => element.remove());
  }

  private handleWidgetLoaded(botKey: string, loadSequence: number): void {
    this.markWidgetArtifacts(loadSequence);

    if (loadSequence !== this.loadSequence) {
      this.removeWidgetArtifactsBySequence(loadSequence);
      return;
    }

    this.currentBotKey = botKey;
    this.retryAttempts.delete(botKey);
  }

  private handleLoadFailure(botKey: string, loadSequence: number, config: ChatBotConfig): void {
    if (loadSequence !== this.loadSequence) return;

    this.currentBotKey = null;
    this.removeCurrentScripts();
    this.removeCurrentWidgetArtifacts();
    this.clearWidgetConfig();

    const attempts = this.retryAttempts.get(botKey) ?? 0;
    if (attempts >= CHAT_WIDGET_MAX_RETRIES) {
      console.error('Failed to load chat widget', config);
      return;
    }

    this.retryAttempts.set(botKey, attempts + 1);
    this.retryTimeoutId = window.setTimeout(() => {
      this.retryTimeoutId = null;

      const nextConfig = this.authService.isAuthenticated() ? AUTHENTICATED_BOT : PUBLIC_BOT;
      const nextBotKey = `${nextConfig.configUrl}|${nextConfig.widgetUrl}`;
      if (nextBotKey !== botKey) return;

      this.loadBot(nextConfig);
    }, CHAT_WIDGET_RETRY_DELAY_MS);
  }

  private markWidgetArtifacts(loadSequence: number): void {
    document.querySelectorAll(CHAT_WIDGET_ARTIFACT_SELECTOR).forEach((element) => {
      if (element.hasAttribute(CHAT_WIDGET_OWNER_ATTR)) return;

      element.setAttribute(CHAT_WIDGET_OWNER_ATTR, CHAT_WIDGET_OWNER);
      element.setAttribute(CHAT_WIDGET_SEQUENCE_ATTR, String(loadSequence));
    });
  }

  private removeWidgetArtifactsBySequence(loadSequence: number): void {
    document
      .querySelectorAll(
        `[${CHAT_WIDGET_OWNER_ATTR}="${CHAT_WIDGET_OWNER}"][${CHAT_WIDGET_SEQUENCE_ATTR}="${loadSequence}"]`,
      )
      .forEach((element) => element.remove());
  }

  private clearWidgetConfig(): void {
    delete (window as ChatWidgetWindow).CHAT_WIDGET_CONFIG;
  }

  private createScript(src: string): HTMLScriptElement {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = false;
    script.src = src;
    script.setAttribute(CHAT_WIDGET_OWNER_ATTR, CHAT_WIDGET_OWNER);
    return script;
  }
}
